import { useEffect, useState } from 'react';
import { MonitorSmartphone, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUiStore } from '@/stores/ui-store';

// MobileSplash mounts OUTSIDE the RouterProvider (so narrow viewports
// see a single full-screen message that works even if the router errors
// out), which means `@tanstack/react-router`'s <Link> would throw on
// mount. Plain anchors are fine — the router re-hydrates on click.

/**
 * Below 768px, the product shows an "optimized for desktop" splash
 * with a basic entity-search fallback (frontend spec §3). Mobile is not
 * a v1.0 surface — this screen keeps the platform usable on tablets
 * while nudging analysts toward a workstation.
 */
export function MobileSplash() {
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 768,
  );
  const lang = useUiStore((s) => s.lang);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!narrow) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6 bg-[var(--color-canvas)] p-6 text-center text-[var(--color-ink)]"
      role="dialog"
      aria-modal="true"
    >
      <MonitorSmartphone className="size-10 text-[var(--color-accent)]" aria-hidden />
      <div className="space-y-2">
        <h1 className="font-serif text-2xl tracking-tight">
          {lang === 'fa' ? 'برای دسکتاپ بهینه شده' : 'Optimized for desktop'}
        </h1>
        <p className="mx-auto max-w-sm text-sm text-[var(--color-ink-muted)]">
          {lang === 'fa'
            ? 'تجربه کامل جام جم برای ایستگاه کاری تحلیلگر طراحی شده است. روی ≥ ۷۶۸px بازدید کنید.'
            : 'The full Jam-e Jam workspace is designed for an analyst workstation. Revisit on a screen ≥ 768px.'}
        </p>
      </div>

      <form
        className="flex w-full max-w-sm gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const q = query.trim();
          if (!q) return;
          window.location.href = `/entity/${encodeURIComponent(q)}`;
        }}
      >
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute start-2 top-1/2 size-4 -translate-y-1/2 text-[var(--color-ink-muted)]"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={lang === 'fa' ? 'جست‌وجوی موجودیت' : 'Search entity'}
            className="ps-8"
          />
        </div>
        <Button type="submit" variant="accent" size="sm">
          {lang === 'fa' ? 'باز کردن' : 'Open'}
        </Button>
      </form>

      <a
        href="/"
        className="text-xxs uppercase tracking-wider text-[var(--color-ink-muted)] underline-offset-2 hover:text-[var(--color-ink)] hover:underline"
      >
        {lang === 'fa' ? 'ادامه به هر حال' : 'Continue anyway'}
      </a>
    </div>
  );
}
